/**
 * Shared State UI Components
 * Reusable editors for state transitions, transform overrides, and property values.
 * Used by both Properties and Pages behavior editors.
 */

import React, { useId, useState, useEffect } from 'react';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { isPropertyTransition, isTimeTransition, numericTuple, type PropertyValue, type PropertyDeclaration, type EasingFunction, type StateDefinition, type TransformComponent, type TransformOverride, type TransitionConfig } from 'trace-viewer/behaviors';
import { EntityType } from 'trace-viewer/cms';
import { CatalogBrowseButton } from '../components/catalog-asset-picker';
import { declarationUnits, unitSuffix } from './property-units';

export type PropertyType = 'string' | 'number' | 'boolean' | 'color' | 'vec3' | 'vec4';

// ============================================================================
// Shared Styles
// ============================================================================

export const sharedStyles = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '100px',
        flexShrink: 0,
    } as React.CSSProperties,
};

export const EASING_OPTIONS: EasingFunction[] = ['linear', 'easeIn', 'easeOut', 'easeInOut'];

// ============================================================================
// Color Swatch — imperatively syncs .value so the browser swatch always matches
// ============================================================================

const ColorSwatch: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
    // Controlled: a `value` prop keeps the native swatch in lock-step with the
    // resolved display value, so switching keyframes repaints it. The previous
    // uncontrolled (defaultValue + manual ref-sync) version could miss updates —
    // the swatch stayed on the old colour while the text field changed.
    // `<input type="color">` requires a 7-char #RRGGBB; fall back to white for
    // anything else so React doesn't warn or no-op the assignment.
    const safe = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#ffffff';
    return (
        <input
            type="color"
            value={safe}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '24px', height: '22px', padding: 0, border: 'none', cursor: 'pointer', flexShrink: 0 }}
        />
    );
};

// ============================================================================
// Value Editor Component
// ============================================================================

interface ValueEditorProps {
    type: PropertyType;
    value: PropertyValue | undefined;
    onChange: (value: PropertyValue) => void;
    flex?: boolean;
    /** Number constraints from the declaration */
    min?: number;
    max?: number;
    step?: number;
    /** Asset-kind hint from StringDeclaration — renders a catalog browse button when 'material' / 'animation-clip' */
    kind?: string;
    /**
     * Unit suffix ('m' / 'ft') for the declared numbers this editor shows.
     * Rendered as trailing small text on `number` / `vec3` — the only types that
     * carry length units (CANOPY_UNITS §7.2). Ignored for every other type.
     */
    suffix?: string;
}

/** Trailing unit label — kept inline so a row never wraps because of it. */
const UnitSuffix: React.FC<{ suffix?: string }> = ({ suffix }) =>
    suffix ? <span style={{ fontSize: '10px', color: '#7a8', flexShrink: 0 }}>{suffix}</span> : null;

export const ValueEditor: React.FC<ValueEditorProps> = ({ type, value, onChange, flex, min, max, step, kind, suffix }) => {
    switch (type) {
        case 'string':
            if (kind === 'material') {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: flex ? 1 : undefined, minWidth: 0 }}>
                        <input
                            type="text"
                            value={(value as string) || ''}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="Material GLB URL"
                            style={{ ...sharedStyles.input, flex: 1, minWidth: 0 }}
                        />
                        <CatalogBrowseButton
                            entityType={EntityType.Material}
                            onSelect={(asset) => onChange(asset.url)}
                            title="Browse catalog materials"
                        />
                    </div>
                );
            }
            if (kind === 'animation-clip') {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: flex ? 1 : undefined, minWidth: 0 }}>
                        <input
                            type="text"
                            value={(value as string) || ''}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="Animation GLB URL (empty = no clip)"
                            style={{ ...sharedStyles.input, flex: 1, minWidth: 0 }}
                        />
                        <CatalogBrowseButton
                            entityType={EntityType.Animation}
                            onSelect={(asset) => onChange(asset.url)}
                            title="Browse catalog animations"
                        />
                    </div>
                );
            }
            return (
                <input
                    type="text"
                    value={(value as string) || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Value"
                    style={{ ...sharedStyles.input, width: flex ? undefined : '80px', flex: flex ? 1 : undefined, minWidth: 0 }}
                />
            );
        case 'number': {
            const clamp = (v: number) => {
                if (min !== undefined && v < min) return min;
                if (max !== undefined && v > max) return max;
                return v;
            };
            return (
                <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flexShrink: 0 }}>
                    <input
                        type="number"
                        value={(value as number) ?? ''}
                        onChange={(e) => onChange(clamp(parseFloat(e.target.value) || 0))}
                        placeholder="Value"
                        min={min}
                        max={max}
                        step={step ?? 0.1}
                        style={{ ...sharedStyles.input, width: '70px' }}
                    />
                    <UnitSuffix suffix={suffix} />
                </div>
            );
        }
        case 'boolean':
            return (
                <input
                    type="checkbox"
                    checked={(value as boolean) ?? false}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', marginLeft: '4px' }}
                />
            );
        case 'color': {
            const hexValue = (value as string)?.startsWith('#')
                ? (value as string).substring(0, 7)
                : '#ffffff';
            return (
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flex: flex ? 1 : undefined, minWidth: 0 }}>
                    <ColorSwatch value={hexValue} onChange={onChange} />
                    <input
                        type="text"
                        value={(value as string) || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="#RRGGBB"
                        style={{ ...sharedStyles.input, width: flex ? undefined : '60px', flex: flex ? 1 : undefined, minWidth: 0 }}
                    />
                </div>
            );
        }
        case 'vec3': {
            const vec = (value as [number, number, number]) || [0, 0, 0];
            return (
                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                    <span style={{ color: '#f88', fontSize: '10px' }}>X</span>
                    <input type="number" value={vec[0]} onChange={(e) => onChange([parseFloat(e.target.value) || 0, vec[1], vec[2]])} step={0.1} style={{ ...sharedStyles.input, width: '45px' }} />
                    <span style={{ color: '#8f8', fontSize: '10px' }}>Y</span>
                    <input type="number" value={vec[1]} onChange={(e) => onChange([vec[0], parseFloat(e.target.value) || 0, vec[2]])} step={0.1} style={{ ...sharedStyles.input, width: '45px' }} />
                    <span style={{ color: '#88f', fontSize: '10px' }}>Z</span>
                    <input type="number" value={vec[2]} onChange={(e) => onChange([vec[0], vec[1], parseFloat(e.target.value) || 0])} step={0.1} style={{ ...sharedStyles.input, width: '45px' }} />
                    <UnitSuffix suffix={suffix} />
                </div>
            );
        }
        case 'vec4': {
            const vec = (value as [number, number, number, number]) || [0, 0, 0, 1];
            return (
                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                    <span style={{ color: '#f88', fontSize: '10px' }}>X</span>
                    <input type="number" value={vec[0]} onChange={(e) => onChange([parseFloat(e.target.value) || 0, vec[1], vec[2], vec[3]])} step={0.01} style={{ ...sharedStyles.input, width: '42px' }} />
                    <span style={{ color: '#8f8', fontSize: '10px' }}>Y</span>
                    <input type="number" value={vec[1]} onChange={(e) => onChange([vec[0], parseFloat(e.target.value) || 0, vec[2], vec[3]])} step={0.01} style={{ ...sharedStyles.input, width: '42px' }} />
                    <span style={{ color: '#88f', fontSize: '10px' }}>Z</span>
                    <input type="number" value={vec[2]} onChange={(e) => onChange([vec[0], vec[1], parseFloat(e.target.value) || 0, vec[3]])} step={0.01} style={{ ...sharedStyles.input, width: '42px' }} />
                    <span style={{ color: '#ff8', fontSize: '10px' }}>W</span>
                    <input type="number" value={vec[3]} onChange={(e) => onChange([vec[0], vec[1], vec[2], parseFloat(e.target.value) || 0])} step={0.01} style={{ ...sharedStyles.input, width: '42px' }} />
                </div>
            );
        }
        default:
            return null;
    }
};

// ============================================================================
// Transition Editor Component
// ============================================================================

interface TransitionEditorProps {
    transition: TransitionConfig | undefined;
    propertyNames: string[];
    /** Pseudo-states (:hover, :active) only support time-driven transitions */
    isPseudo: boolean;
    onChange: (transition: TransitionConfig | undefined) => void;
}

export const TransitionEditor: React.FC<TransitionEditorProps> = ({ transition, propertyNames, isPseudo, onChange }) => {
    const datalistId = useId();
    const isDriver = transition ? isPropertyTransition(transition) : false;
    const isTime = transition ? isTimeTransition(transition) : false;

    const modeToggleStyle: React.CSSProperties = {
        background: 'none', border: '1px solid #444', borderRadius: '2px',
        color: '#888', cursor: 'pointer', fontSize: '9px', padding: '1px 4px',
    };
    const activeModeStyle: React.CSSProperties = {
        ...modeToggleStyle, borderColor: '#666', color: '#ccc', background: '#444',
    };
    const compactInput: React.CSSProperties = {
        ...sharedStyles.input, width: '45px', fontSize: '10px', padding: '2px 4px',
    };
    const compactSelect: React.CSSProperties = {
        ...sharedStyles.input, fontSize: '10px', padding: '2px 4px', cursor: 'pointer',
    };

    if (!transition) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <label style={{ fontSize: '11px', color: '#777', minWidth: '60px' }}>Transition</label>
                <button
                    onClick={() => onChange({ duration: 0.3, easing: 'easeOut' })}
                    style={{ background: 'none', border: '1px solid #444', borderRadius: '2px', color: '#666', cursor: 'pointer', fontSize: '10px', padding: '1px 6px' }}
                >+ add</button>
            </div>
        );
    }

    const switchToTime = () => onChange({ duration: 0.3, easing: transition.easing ?? 'easeOut' });
    const switchToDriver = () => onChange({ driver: propertyNames[0] ?? '', range: [0, 1], easing: transition.easing ?? 'linear' });

    return (
        <div style={{ marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '11px', color: '#777', minWidth: '60px' }}>Transition</label>
                {/* Mode toggle — only show for custom states, not pseudo-states */}
                {!isPseudo && (
                    <div style={{ display: 'flex', gap: '1px' }}>
                        <button onClick={switchToTime} style={isTime ? activeModeStyle : modeToggleStyle} title="Time-driven transition">Time</button>
                        <button
                            onClick={switchToDriver}
                            disabled={propertyNames.length === 0}
                            style={isDriver ? activeModeStyle : { ...modeToggleStyle, opacity: propertyNames.length === 0 ? 0.4 : 1 }}
                            title={propertyNames.length === 0 ? 'No properties available' : 'Property-driven transition'}
                        >Prop</button>
                    </div>
                )}
                {/* Time-driven fields */}
                {isTime && isTimeTransition(transition) && (
                    <>
                        <input
                            type="number"
                            value={transition.duration}
                            onChange={(e) => onChange({ ...transition, duration: Math.max(0, parseFloat(e.target.value) || 0) })}
                            step={0.1} min={0}
                            style={compactInput}
                            title="Duration (seconds)"
                        />
                        <span style={{ fontSize: '10px', color: '#555' }}>s</span>
                        <span style={{ fontSize: '10px', color: '#555', marginLeft: '4px' }}>delay</span>
                        <input
                            type="number"
                            value={transition.delay ?? 0}
                            onChange={(e) => {
                                const delay = Math.max(0, parseFloat(e.target.value) || 0);
                                onChange({ ...transition, delay: delay > 0 ? delay : undefined });
                            }}
                            step={0.1} min={0}
                            style={compactInput}
                            title="Delay before transition starts (seconds)"
                        />
                        <span style={{ fontSize: '10px', color: '#555' }}>s</span>
                    </>
                )}
                {/* Property-driven: driver name (editable with suggestions) */}
                {isDriver && isPropertyTransition(transition) && (
                    <>
                        <input
                            list={datalistId}
                            value={transition.driver}
                            onChange={(e) => onChange({ ...transition, driver: e.target.value })}
                            placeholder="property"
                            style={{ ...compactInput, width: '80px' }}
                            title="Driver property name (type or pick from list)"
                        />
                        <datalist id={datalistId}>
                            {propertyNames.map(n => <option key={n} value={n} />)}
                        </datalist>
                    </>
                )}
                {/* Easing — shared by both modes */}
                <select
                    value={transition.easing ?? (isDriver ? 'linear' : 'easeInOut')}
                    onChange={(e) => onChange({ ...transition, easing: e.target.value as EasingFunction })}
                    style={compactSelect}
                >
                    {EASING_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
                {/* Remove */}
                <button
                    onClick={() => onChange(undefined)}
                    style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '10px', padding: '0 3px' }}
                    title="Remove transition"
                >x</button>
            </div>
            {/* Range row for property-driven transitions */}
            {isDriver && isPropertyTransition(transition) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', paddingLeft: '66px' }}>
                    <span style={{ fontSize: '10px', color: '#666' }}>range</span>
                    <input
                        type="number"
                        value={transition.range?.[0] ?? 0}
                        onChange={(e) => {
                            const r: [number, number] = [parseFloat(e.target.value) || 0, transition.range?.[1] ?? 1];
                            onChange({ ...transition, range: r });
                        }}
                        step={0.1}
                        style={compactInput}
                        title="Range start (maps to progress 0)"
                    />
                    <span style={{ fontSize: '10px', color: '#555' }}>→</span>
                    <input
                        type="number"
                        value={transition.range?.[1] ?? 1}
                        onChange={(e) => {
                            const r: [number, number] = [transition.range?.[0] ?? 0, parseFloat(e.target.value) || 0];
                            onChange({ ...transition, range: r });
                        }}
                        step={0.1}
                        style={compactInput}
                        title="Range end (maps to progress 1)"
                    />
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Transform Override Editor
// ============================================================================

export const TransformOverrideEditor: React.FC<{
    transform: TransformOverride;
    onChange: (t: TransformOverride) => void;
    onRemove: () => void;
}> = ({ transform, onChange, onRemove }) => {
    const renderVec3Row = (label: string, field: 'position' | 'scale', values: [number, number, number], step: number) => {
        const colors = ['#f88', '#8f8', '#88f'];
        const labels = ['X', 'Y', 'Z'];
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                <span style={{ fontSize: '10px', color: '#666', minWidth: '28px' }}>{label}</span>
                {[0, 1, 2].map(i => (
                    <React.Fragment key={i}>
                        <span style={{ fontSize: '8px', color: colors[i] }}>{labels[i]}</span>
                        <input
                            type="number"
                            value={values[i]}
                            onChange={(e) => {
                                const v = [...values] as [number, number, number];
                                v[i] = parseFloat(e.target.value) || 0;
                                onChange({ ...transform, [field]: v });
                            }}
                            step={step}
                            style={{ ...sharedStyles.input, width: '36px', fontSize: '9px', padding: '1px 3px' }}
                        />
                    </React.Fragment>
                ))}
                <button
                    onClick={() => { const { [field]: _, ...rest } = transform; onChange(rest); }}
                    style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '9px', padding: '0 2px' }}
                >x</button>
            </div>
        );
    };

    /**
     * A component can be a binding (`"{stageHeight}"`), which has no value to
     * put in a number input — the legacy-constraint migration authors those.
     * Show it, don't try to edit it.
     */
    const renderBoundRow = (label: string, values: readonly TransformComponent[]) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
            <span style={{ fontSize: '10px', color: '#666', minWidth: '28px' }}>{label}</span>
            <span style={{ fontSize: '9px', color: '#8a8', fontFamily: 'monospace' }}>
                {values.map(v => String(v)).join(', ')}
            </span>
        </div>
    );

    /** The Euler editor where the quaternion is numeric, read-only where bound. */
    const renderRotationRowOrBinding = (values: readonly TransformComponent[]) => {
        const numeric = numericTuple(values);
        return numeric
            ? renderRotationRow(numeric as [number, number, number, number])
            : renderBoundRow('rot', values);
    };

    /** Numeric row where the tuple allows it, read-only where it is bound. */
    const renderRow = (
        label: string,
        field: 'position' | 'scale',
        values: readonly TransformComponent[],
        step: number,
    ) => {
        const numeric = numericTuple(values);
        return numeric
            ? renderVec3Row(label, field, numeric as [number, number, number], step)
            : renderBoundRow(label, values);
    };

    /** Display quaternion rotation as Euler degrees, convert back on edit */
    const renderRotationRow = (quat: [number, number, number, number]) => {
        const DEG = 180 / Math.PI;
        const RAD = Math.PI / 180;
        const q = new Quaternion(quat[0], quat[1], quat[2], quat[3]);
        const euler = q.toEulerAngles();
        const degrees: [number, number, number] = [
            Math.round(euler.x * DEG * 100) / 100,
            Math.round(euler.y * DEG * 100) / 100,
            Math.round(euler.z * DEG * 100) / 100,
        ];
        const colors = ['#f88', '#8f8', '#88f'];
        const labels = ['X', 'Y', 'Z'];
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                <span style={{ fontSize: '10px', color: '#666', minWidth: '28px' }}>rot</span>
                {[0, 1, 2].map(i => (
                    <React.Fragment key={i}>
                        <span style={{ fontSize: '8px', color: colors[i] }}>{labels[i]}</span>
                        <input
                            type="number"
                            value={degrees[i]}
                            onChange={(e) => {
                                const d = [...degrees] as [number, number, number];
                                d[i] = parseFloat(e.target.value) || 0;
                                const newQ = Quaternion.FromEulerAngles(d[0] * RAD, d[1] * RAD, d[2] * RAD);
                                onChange({ ...transform, rotation: [newQ.x, newQ.y, newQ.z, newQ.w] });
                            }}
                            step={1}
                            style={{ ...sharedStyles.input, width: '36px', fontSize: '9px', padding: '1px 3px' }}
                        />
                    </React.Fragment>
                ))}
                <span style={{ fontSize: '8px', color: '#555' }}>°</span>
                <button
                    onClick={() => { const { rotation: _, ...rest } = transform; onChange(rest); }}
                    style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '9px', padding: '0 2px' }}
                >x</button>
            </div>
        );
    };

    return (
        <div>
            {transform.position && renderRow('pos', 'position', transform.position, 0.1)}
            {transform.rotation && renderRotationRowOrBinding(transform.rotation)}
            {transform.scale && renderRow('scl', 'scale', transform.scale, 0.1)}
            <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                {!transform.position && (
                    <button onClick={() => onChange({ ...transform, position: [0, 0, 0] })}
                        style={{ background: 'none', border: '1px solid #444', borderRadius: '2px', color: '#555', cursor: 'pointer', fontSize: '9px', padding: '1px 4px' }}>+ pos</button>
                )}
                {!transform.rotation && (
                    <button onClick={() => onChange({ ...transform, rotation: [0, 0, 0, 1] })}
                        style={{ background: 'none', border: '1px solid #444', borderRadius: '2px', color: '#555', cursor: 'pointer', fontSize: '9px', padding: '1px 4px' }}>+ rot</button>
                )}
                {!transform.scale && (
                    <button onClick={() => onChange({ ...transform, scale: [1, 1, 1] })}
                        style={{ background: 'none', border: '1px solid #444', borderRadius: '2px', color: '#555', cursor: 'pointer', fontSize: '9px', padding: '1px 4px' }}>+ scl</button>
                )}
                <button onClick={onRemove}
                    style={{ background: 'none', border: 'none', color: '#a55', cursor: 'pointer', fontSize: '9px', padding: '0 2px', marginLeft: 'auto' }}>remove transform</button>
            </div>
        </div>
    );
};

// ============================================================================
// State Name Input
// ============================================================================

export const StateNameInput: React.FC<{
    name: string;
    allNames: string[];
    onRename: (newName: string) => void;
    disabled?: boolean;
}> = ({ name, allNames, onRename, disabled }) => {
    const [value, setValue] = useState(name);
    // Sync if the name changes externally
    useEffect(() => { setValue(name); }, [name]);

    const handleSubmit = () => {
        const trimmed = value.trim();
        if (trimmed && trimmed !== name && !allNames.includes(trimmed)) {
            onRename(trimmed);
        } else {
            setValue(name);
        }
    };

    return (
        <input
            value={value}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleSubmit}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') setValue(name); }}
            disabled={disabled}
            style={{
                ...sharedStyles.input, flex: 1, fontSize: '11px', padding: '2px 4px',
                backgroundColor: 'transparent', border: '1px solid transparent',
                color: disabled ? '#8af' : '#fff',
            }}
            title={disabled ? 'The default state cannot be renamed' : 'State name'}
        />
    );
};

// ============================================================================
// State Detail Editor
// Renders the expanded content for a single state: transition, values, transform.
// ============================================================================

interface StateDetailEditorProps {
    state: StateDefinition;
    stateName: string;
    propertyNames: string[];
    propertyDefs: Map<string, PropertyDeclaration>;
    onChange: (state: StateDefinition) => void;
}

export const StateDetailEditor: React.FC<StateDetailEditorProps> = ({ state, stateName, propertyNames, propertyDefs, onChange }) => {
    const isPseudo = stateName.startsWith(':');

    return (
        <div style={{ padding: '6px 8px', borderTop: '1px solid #333' }}>
            {/* Transition config */}
            <TransitionEditor
                transition={state.transition}
                propertyNames={propertyNames}
                isPseudo={isPseudo}
                onChange={(t: TransitionConfig | undefined) => {
                    if (t) {
                        onChange({ ...state, transition: t });
                    } else {
                        const next: StateDefinition = { values: state.values };
                        if (state.transform) next.transform = state.transform;
                        onChange(next);
                    }
                }}
            />

            {/* Property value overrides */}
            {propertyNames.length > 0 && (
                <>
                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Values</div>
                    {Object.entries(state.values).map(([propName, val]) => {
                        // Annotated (not asserted) union widening: min/max/step/kind exist only on some
                        // PropertyDeclaration members, but ValueEditor takes them all as optional.
                        const propDef: (PropertyDeclaration & { min?: number; max?: number; step?: number; kind?: string }) | undefined =
                            propertyDefs.get(propName);
                        const propType = (propDef?.type ?? 'string');
                        return (
                            <div key={propName} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                <span style={{ fontSize: '11px', color: '#aaa', minWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{propName}</span>
                                <ValueEditor
                                    type={propType}
                                    value={val}
                                    onChange={(newVal) => onChange({ ...state, values: { ...state.values, [propName]: newVal } })}
                                    flex
                                    min={propDef?.min}
                                    max={propDef?.max}
                                    step={propDef?.step}
                                    kind={propDef?.kind}
                                    // State override values live in the declaration's
                                    // declared space (CANOPY_UNITS §3), so they wear its unit.
                                    suffix={unitSuffix(declarationUnits(propDef))}
                                />
                                <button
                                    onClick={() => {
                                        const { [propName]: _, ...rest } = state.values;
                                        onChange({ ...state, values: rest });
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '10px', padding: '0 2px' }}
                                >x</button>
                            </div>
                        );
                    })}
                    {/* Add value override */}
                    {propertyNames.filter(n => !(n in state.values)).length > 0 && (
                        <select
                            value=""
                            onChange={(e) => {
                                if (!e.target.value) return;
                                const propDef = propertyDefs.get(e.target.value);
                                const baseVal = propDef ? propDef.value : '';
                                onChange({ ...state, values: { ...state.values, [e.target.value]: baseVal } });
                            }}
                            style={{ ...sharedStyles.input, fontSize: '10px', padding: '2px 4px', width: '100%', color: '#666', cursor: 'pointer' }}
                        >
                            <option value="">+ add property override...</option>
                            {propertyNames.filter(n => !(n in state.values)).map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    )}
                </>
            )}

            {/* Transform override */}
            <div style={{ fontSize: '10px', color: '#666', marginTop: '6px', marginBottom: '4px' }}>Transform</div>
            {state.transform ? (
                <TransformOverrideEditor
                    transform={state.transform}
                    onChange={(t) => onChange({ ...state, transform: t })}
                    onRemove={() => { const { transform: _, ...rest } = state; onChange(rest); }}
                />
            ) : (
                <button
                    onClick={() => onChange({ ...state, transform: {} })}
                    style={{ background: 'none', border: '1px solid #444', borderRadius: '2px', color: '#666', cursor: 'pointer', fontSize: '10px', padding: '1px 6px' }}
                >+ add transform</button>
            )}

            {/* Visibility (active) override. undefined = no change; true = shown;
                false = hidden when this state is active. Mirrors the gizmo/hotkey
                active capture. */}
            <div style={{ fontSize: '10px', color: '#666', marginTop: '6px', marginBottom: '4px' }}>Visibility</div>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {([
                    [undefined, 'inherit'],
                    [true, 'shown'],
                    [false, 'hidden'],
                ] as const).map(([val, label]) => (
                    <button
                        key={label}
                        onClick={() => {
                            if (val === undefined) { const { active: _, ...rest } = state; onChange(rest); }
                            else onChange({ ...state, active: val });
                        }}
                        style={{
                            background: state.active === val ? '#1c3a59' : 'none',
                            border: `1px solid ${state.active === val ? '#4a90d9' : '#444'}`,
                            borderRadius: '2px',
                            color: state.active === val ? '#fff' : '#888',
                            cursor: 'pointer', fontSize: '10px', padding: '1px 8px',
                        }}
                    >{label}</button>
                ))}
            </div>
        </div>
    );
};
